// src/components/CollectionsList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import DeleteCollectionModal from "./DeleteCollectionModal";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import CollectionTypeBadge, { shouldShowBadge } from "./CollectionTypeBadge";
import CollectionTransferModal from "./CollectionTransferModal";
import CollectionsListActionsSidebar from "./CollectionsListActionsSidebar";

export const CollectionsList = () => {
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const [delId, setDelId] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // ─── Filtre par type + vue active ───
  const [typeFilter, setTypeFilter] = useState("all"); // all | private | inalienable | deposit | own | shared
  const [isFoundation, setIsFoundation] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const fetchCollections = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!user) return;

      // Détecter role et foundation_mode
      const { data: profile } = await supabase
        .from("profiles")
        .select("role, foundation_mode")
        .eq("id", user.id)
        .maybeSingle();

      const isAdvisor = profile?.role === "expert";
      const foundationMode = profile?.foundation_mode === true;
      setIsAdvisor(isAdvisor);
      setIsFoundation(foundationMode);

      let allCollections = [];

      if (isAdvisor) {
        // Advisor : ses propres collections + celles partagées via collection_permissions
        const [{ data: perms }, { data: ownData, error: ownError }] = await Promise.all([
          supabase
            .from("collection_permissions")
            .select("collection_id, client_id, can_view, can_edit")
            .eq("grantee_id", user.id),
          supabase
            .from("collections")
            .select("*")
            .eq("client_id", user.id),
        ]);

        if (ownError) throw ownError;

        const ownCollections = (ownData || []).map((col) => ({
          ...col,
          _sharedRole: null,
          _isShared: false,
        }));

        const collectionIds = (perms || []).map((p) => p.collection_id).filter(Boolean);
        let sharedCollections = [];

        if (collectionIds.length > 0) {
          const { data, error } = await supabase
            .from("collections")
            .select("*")
            .in("id", collectionIds);
          if (error) throw error;

          sharedCollections = (data || [])
            .filter((col) => col.client_id !== user.id) // éviter doublons avec ownCollections
            .map((col) => {
              const perm = perms.find((p) => p.collection_id === col.id);
              return { ...col, _sharedRole: perm?.can_edit ? "modification" : "lecture", _isShared: true };
            });
        }

        allCollections = [...ownCollections, ...sharedCollections];
      } else {
        // Collecteur standard : ses propres collections
        const { data, error } = await supabase
          .from("collections")
          .select("*")
          .eq("client_id", user.id);
        if (error) throw error;
        allCollections = data || [];
      }

      // Compter les œuvres + sommer les valeurs estimées pour chaque collection
      const collectionsWithCounts = await Promise.all(
        allCollections.map(async (collection) => {
          // count
          const { count, error: countError } = await supabase
            .from("artworks_secure")
            .select("id", { count: "exact", head: true })
            .eq("collection_id", collection.id);
          if (countError) throw countError;

          // somme estimée (peut renvoyer [] si aucune œuvre — c'est OK)
          const { data: valsData } = await supabase
            .from("artworks_secure")
            .select("estimated_value, current_estimated_value")
            .eq("collection_id", collection.id);
          const totalEstimated = (valsData || []).reduce(
            (sum, a) => sum + (parseFloat(a.current_estimated_value) || parseFloat(a.estimated_value) || 0),
            0
          );

          return {
            ...collection,
            artworkCount: count || 0,
            totalEstimated,
            // Garde-fou : collection_type peut être absent sur les anciennes collections
            collection_type: collection.collection_type || "private",
          };
        })
      );

      setCollections(collectionsWithCounts);
    } catch (error) {
      console.error("Erreur lors du chargement des collections:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const handleCreateCollection = async () => {
    if (!user) return;

    // 1. créer la collection brouillon par défaut
    const { data, error } = await supabase
      .from("collections")
      .insert([
        {
          client_id: user.id,
          title: t("collectionsList.defaultTitle"),
          description: null,
          image_url: "/images/collections/ACLASSER.png", // fallback par défaut
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Erreur création collection:", error);
      return;
    }

    // 2. redirection vers l’édition de cette nouvelle collection
    navigate(`/collections/${data.id}/edit`);
  };

  const normalizeImageUrl = (raw) => {
    const fallback = "/images/collections/ACLASSER.png";
    if (!raw) return fallback;
    const url = String(raw).trim();
    if (!url) return fallback;

    // valeurs legacy / bizarres
    if (url === "placeholder.png" || url === "/placeholder.png") return fallback;

    // URL absolue OK
    if (url.startsWith("http://") || url.startsWith("https://")) return url;

    // chemin absolu OK
    if (url.startsWith("/")) return url;

    // chemin relatif => on le rend absolu pour éviter /collections/placeholder.png etc.
    return `/${url}`;
  };

  const getImageWithFallback = (collection) => {
    return normalizeImageUrl(
      collection?.image_url ||
        (typeof collection?.image === "string" ? collection.image : null) ||
        collection?.image?.file ||
        collection?.image?.path
    );
  };

  // ─── Calculs consolidés (patrimoine, comptes par type) ───
  const stats = useMemo(() => {
    const out = {
      total: collections.length,
      totalArtworks: 0,
      patrimonialValue: 0,
      // Collectionneur
      depositCount: 0,
      inalienableCount: 0,
      privateCount: 0,
      byType: {
        private: 0, inalienable: 0, deposit: 0,
        // Fondation
        foundation_owned: 0, foundation_founder: 0,
        foundation_donation: 0, foundation_deposit: 0,
      },
      // Fondation — compteurs dédiés
      foundationOwnedCount: 0,    // œuvres propriété propre
      foundationExternalCount: 0, // œuvres dont la fondation n'est pas propriétaire
    };
    for (const c of collections) {
      out.totalArtworks += c.artworkCount || 0;
      const tp = c.collection_type || "private";
      out.byType[tp] = (out.byType[tp] || 0) + 1;

      // ── Collectionneur ──
      if (tp === "deposit") {
        out.depositCount += c.artworkCount || 0;
      } else if (tp === "inalienable") {
        out.inalienableCount += c.artworkCount || 0;
        out.patrimonialValue += c.totalEstimated || 0;
      } else if (tp === "private") {
        out.privateCount += c.artworkCount || 0;
        out.patrimonialValue += c.totalEstimated || 0;
      }
      // ── Fondation ──
      else if (tp === "foundation_owned") {
        out.foundationOwnedCount += c.artworkCount || 0;
        out.patrimonialValue += c.totalEstimated || 0; // seul type valorisé
      } else if (tp === "foundation_founder" || tp === "foundation_donation" || tp === "foundation_deposit") {
        out.foundationExternalCount += c.artworkCount || 0;
        // pas inclus dans patrimonialValue — non réalisables
      }
    }
    return out;
  }, [collections]);

  // Collections filtrées selon le filtre actif
  const filteredCollections = useMemo(() => {
    if (typeFilter === "own") return collections.filter((c) => !c._isShared);
    if (typeFilter === "shared") return collections.filter((c) => c._isShared);
    if (typeFilter === "all") return collections;
    return collections.filter(
      (c) => (c.collection_type || "private") === typeFilter
    );
  }, [collections, typeFilter]);

  // Formatter pour les montants
  const fmt = (n) =>
    new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || undefined, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n || 0);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex mb-8">
          <h1 className="text-3xl font-serif">
            {t("collectionsList.title")}
          </h1>
        </div>

        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>

        {/* ─── Bandeau patrimoine consolidé ─── */}
        {collections.length > 0 && (
          <div
            className="bg-white border border-gray-200 mb-8"
            style={{ padding: "28px 36px", fontFamily: "Helvetica Neue, sans-serif" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 0,
              }}
            >
              <div style={{ paddingRight: 32, borderRight: "1px solid #EFECE7" }}>
                <div style={{ fontSize: 11, color: "#6B6B6B", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 10 }}>
                  {isFoundation ? t("collectionsList.stats.foundationValue") : t("collectionsList.stats.patrimonialValue")}
                </div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: "#1A1A1A" }}>
                  {fmt(stats.patrimonialValue)}
                </div>
                <div style={{ fontSize: 12, color: "#A8A8A8", marginTop: 4, fontStyle: "italic" }}>
                  {isFoundation ? t("collectionsList.stats.excludeLoans") : t("collectionsList.stats.excludeDeposits")}
                </div>
              </div>

              <div style={{ paddingRight: 32, paddingLeft: 32, borderRight: "1px solid #EFECE7" }}>
                <div style={{ fontSize: 11, color: "#6B6B6B", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 10 }}>
                  {t("collectionsList.stats.totalArtworks")}
                </div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: "#1A1A1A" }}>
                  {stats.totalArtworks}
                </div>
                <div style={{ fontSize: 12, color: "#A8A8A8", marginTop: 4, fontStyle: "italic" }}>
                  {t("collectionsList.collectionCount", { count: stats.total })}
                </div>
              </div>

              {/* « Inaliénable » = régime fondation/institution → masqué pour un collectionneur. */}
              {isFoundation && (
                <div style={{ paddingRight: 32, paddingLeft: 32, borderRight: "1px solid #EFECE7" }}>
                  <div style={{ fontSize: 11, color: "#6B6B6B", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 10 }}>
                    {t("collectionsList.stats.foundationOwned")}
                  </div>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: "#1A1A1A" }}>
                    {stats.foundationOwnedCount}
                  </div>
                  <div style={{ fontSize: 12, color: "#A8A8A8", marginTop: 4, fontStyle: "italic" }}>
                    {t("collectionsList.stats.foundationOwnedSubtitle")}
                  </div>
                </div>
              )}

              <div style={{ paddingLeft: 32 }}>
                <div style={{ fontSize: 11, color: "#6B6B6B", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 10 }}>
                  {isFoundation ? t("collectionsList.stats.foundationLoans") : t("collectionsList.stats.deposit")}
                </div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: "#1A1A1A" }}>
                  {isFoundation ? stats.foundationExternalCount : stats.depositCount}
                </div>
                <div style={{ fontSize: 12, color: "#A8A8A8", marginTop: 4, fontStyle: "italic" }}>
                  {t("collectionsList.stats.nonPatrimonial")}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Onglets de filtre par type ─── */}
        {collections.length > 0 && (
          <div
            style={{
              borderBottom: "1px solid #E5E2DD",
              marginBottom: 32,
              display: "flex",
              gap: 32,
              fontFamily: "Helvetica Neue, sans-serif",
            }}
          >
            {(isAdvisor ? [
              { value: "all",    label: t("round36.collectionsList.tabs.all"),              count: stats.total },
              { value: "own",    label: t("round36.collectionsList.tabs.own"),     count: collections.filter((c) => !c._isShared).length },
              { value: "shared", label: t("round36.collectionsList.tabs.shared"), count: collections.filter((c) => c._isShared).length },
            ] : isFoundation ? [
              { value: "all",                  label: t("collectionsList.tabs.all"),               count: stats.total },
              { value: "foundation_owned",     label: t("collectionsList.tabs.foundationOwned"),   count: stats.byType.foundation_owned },
              { value: "foundation_founder",   label: t("collectionsList.tabs.foundationFounder"), count: stats.byType.foundation_founder },
              { value: "foundation_donation",  label: t("collectionsList.tabs.foundationDonation"),count: stats.byType.foundation_donation },
              { value: "foundation_deposit",   label: t("collectionsList.tabs.foundationDeposit"), count: stats.byType.foundation_deposit },
            ] : [
              // Collectionneur : « inaliénable » (régime fondation) retiré ; « en dépôt » gardé.
              { value: "all",         label: t("collectionsList.tabs.all"),         count: stats.total },
              { value: "private",     label: t("collectionsList.tabs.private"),     count: stats.byType.private },
              { value: "deposit",     label: t("collectionsList.tabs.deposit"),     count: stats.byType.deposit },
            ]).map((tab) => {
              const isActive = typeFilter === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setTypeFilter(tab.value)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "14px 0",
                    fontSize: 14,
                    color: isActive ? "#1A1A1A" : "#6B6B6B",
                    cursor: "pointer",
                    borderBottom: isActive ? "2px solid #C9A961" : "2px solid transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    transition: "all 0.2s",
                  }}
                >
                  {tab.label}
                  <span
                    style={{
                      fontSize: 11,
                      color: isActive ? "#2D2D2D" : "#A8A8A8",
                      background: isActive ? "#E5D5A8" : "#EFECE7",
                      padding: "2px 8px",
                    }}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCollections.length > 0 ? (
            filteredCollections.map((collection) => (
              <div
                key={collection.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-medium"
                style={{ position: "relative" }}
              >
                {/* Badge type de collection (sauf type "normal" selon le mode) */}
                {shouldShowBadge(collection.collection_type, isFoundation) && (
                  <div
                    style={{
                      position: "absolute",
                      top: 14,
                      left: 14,
                      zIndex: 3,
                    }}
                  >
                    <CollectionTypeBadge
                      type={collection.collection_type}
                      size="medium"
                      isFoundation={isFoundation}
                    />
                  </div>
                )}

                <div className="relative group">
                  <div className="w-full overflow-hidden" style={{ height: 220 }}>
                    <img
                      src={getImageWithFallback(collection)}
                      alt={collection.title}
                      className="w-full h-full object-cover transition duration-300 group-hover:opacity-90"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                    <div className="flex gap-2">
                      <Link
                        to={`/collections/${collection.id}`}
                        className="bg-white text-black p-2 rounded-full hover:bg-gray-200"
                        title={t("collectionsList.viewCollection")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                        </svg>
                      </Link>

<Link
  to={`/collections/${collection.id}/edit`}
  className="bg-white text-black p-2 rounded-full hover:bg-gray-200"
  title={t("collectionsList.editCollection")}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>
</Link>
                    </div>
                  </div>
                </div>

                {/* Badge accès partagé */}
                {collection._isShared && (
                  <div className="px-4 pt-3 flex gap-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded uppercase tracking-wide"
                      style={{ backgroundColor: collection._sharedRole === "modification" ? "#166534" : "#1e3a5f", color: "#fff" }}>
                      {collection._sharedRole === "modification" ? t("collectionsList.accessEdit") : t("collectionsList.accessView")}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded uppercase tracking-wide bg-gray-700 text-white">
                      {t("collectionsList.sharedBadge")}
                    </span>
                  </div>
                )}

                <div className="p-4">
                  <h2 className="text-xl font-serif mb-2">
                    {collection.title}
                  </h2>
                  <p className="text-gray-dark">
                    {collection.artist} •{" "}
                    {t("collectionsList.artworkCount", {
                      count: collection.artworkCount,
                    })}
                  </p>
                </div>

                <div className="p-4 pt-0 flex items-center justify-between">
                  <Link
                    to={`/collections-reports/${collection.id}`}
                    className="text-xs border border-amber-600 text-amber-700 px-3 py-1.5 rounded hover:bg-amber-50 transition"
                  >
                    {t("collectionsList.downloadReport")}
                  </Link>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {/* Transférer vers un client — advisor uniquement, collections propres */}
                    {isAdvisor && !collection._isShared && (
                      <button
                        onClick={() => setShowTransferModal(true)}
                        title={t("round36.collectionsList.transfer.title")}
                        style={{
                          fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                          border: "1px solid #c9a961", color: "#c9a961", background: "#fff",
                          fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap",
                        }}
                      >
                        {t("round36.collectionsList.transfer.button")} →
                      </button>
                    )}
                    {/* Supprimer seulement si propriétaire */}
                    {!collection._isShared && (
                      <button
                        className="btn-icon-danger"
                        onClick={() => setDelId(collection.id)}
                        title={t("collectionsList.deleteCollection")}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : collections.length > 0 ? (
            // Filtre actif mais aucun résultat
            <div className="col-span-3 flex flex-col items-center justify-center py-16 text-center">
              <p className="text-lg font-serif text-gray-700 mb-3">
                {t("collectionsList.noTypeTitle")}
              </p>
              <p className="text-gray-500 mb-6 max-w-md text-sm">
                {t("collectionsList.noTypeSubtitle")}
              </p>
              <button
                type="button"
                onClick={() => setTypeFilter("all")}
                className="inline-flex items-center px-5 py-2.5 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition"
              >
                {t("collectionsList.showAll")}
              </button>
            </div>
          ) : (
            <div className="col-span-3 flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-6 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto h-24 w-24"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>

              <p className="text-xl font-serif text-gray-700 mb-3">
                {t("collectionsList.noneTitle")}
              </p>
              <p className="text-gray-500 mb-6 max-w-md">
                {t("collectionsList.noneSubtitle")}
              </p>

              <button
                onClick={handleCreateCollection}
                className="inline-flex items-center px-5 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {t("collectionsList.createFirst")}
              </button>
            </div>
          )}
        </div>
        </div>

        <CollectionsListActionsSidebar t={t} onCreateCollection={handleCreateCollection} />
        </div>
      </div>

      {/* Modal transfert de collection */}
      {showTransferModal && (
        <CollectionTransferModal
          ownCollections={collections.filter((c) => !c._isShared)}
          onClose={() => setShowTransferModal(false)}
        />
      )}

      {/* Modale globale */}
      <DeleteCollectionModal
        open={!!delId}
        onClose={() => setDelId(null)}
        collectionId={delId}
        clientId={user?.id}
        supabase={supabase}
        onDeleted={() => {
          setDelId(null);
          fetchCollections();
        }}
      />
    </>
  );
};

export default CollectionsList;